import { supabase } from '@/lib/supabaseClient';
import type { TicketRow, TicketStatus, TicketPriority, TicketMessage } from '@/types/support';
import { logAdmin } from './auditService';
import { postEventSilent } from '@/services/webhookService';
import { 
  sendTransactionalEmail, 
  prepareNewTicketAdminEmail,
  prepareNewTicketMessageAdminEmail 
} from '@/services/resendService';

export async function createTicket(params: {
  category: string;
  priority: TicketPriority;
  subject: string;
  message: string;
}): Promise<TicketRow> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utilisateur non authentifié');

    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name, email')
      .eq('user_id', user.id)
      .maybeSingle();

    const { data: ticket, error } = await supabase
      .from('tickets')
      .insert({
        user_id: user.id,
        category: params.category,
        priority: params.priority,
        subject: params.subject,
        message: params.message,
        status: 'OPEN',
        admin_unread: true,
        updated_at: new Date().toISOString(),
      })
      .select('id, user_id, status, category, priority, subject, message, created_at, updated_at')
      .single();

    if (error) {
      if (import.meta.env.DEV) {
        console.error('[supportService] Erreur création ticket:', error);
      }
      throw new Error('Impossible de créer le ticket');
    }

    if (ticket) {
      await supabase.from('ticket_messages').insert({
        ticket_id: ticket.id,
        user_id: user.id,
        is_admin: false,
        message: params.message,
      });

      if (profile?.email && profile?.full_name) {
        try {
          const emailPayload = prepareNewTicketAdminEmail({
            ticketId: ticket.id,
            userName: profile.full_name,
            userEmail: profile.email,
            category: params.category,
            priority: params.priority,
            subject: params.subject,
            message: params.message,
          });
          await sendTransactionalEmail(emailPayload);
          if (import.meta.env.DEV) {
            console.log(`[supportService] ✅ Email envoyé à l'admin pour nouveau ticket #${ticket.id}`);
          }
        } catch (emailError) {
          if (import.meta.env.DEV) {
            console.warn('[supportService] ⚠️ Erreur envoi email admin nouveau ticket:', emailError);
          }
        }
      }
    }

    await logAdmin('TICKET_CREATE', 'ticket', ticket?.id ?? 0, { category: params.category, priority: params.priority }).catch(() => {});
    await postEventSilent('ticket.create', { category: params.category, priority: params.priority });

    return {
      id: ticket!.id,
      userId: ticket!.user_id,
      status: (ticket!.status as TicketStatus) || 'OPEN',
      category: ticket!.category || 'GENERAL',
      priority: (ticket!.priority as TicketPriority) || 'NORMAL',
      subject: ticket!.subject || '',
      message: ticket!.message || '',
      createdAt: ticket!.created_at,
      updatedAt: ticket!.updated_at,
    };
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('Erreur inconnue lors de la création du ticket');
  }
}

export async function listMyTickets(): Promise<TicketRow[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utilisateur non authentifié');

    const { data, error } = await supabase
      .from('tickets')
      .select('id, user_id, status, category, priority, subject, message, created_at, updated_at, unread_count')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(50);

    if (error) {
      if (import.meta.env.DEV) {
        console.error('[supportService] Erreur récupération tickets:', error);
      }
      return [];
    }

    return (data || []).map((ticket) => ({
      id: ticket.id,
      userId: ticket.user_id,
      status: (ticket.status as TicketStatus) || 'OPEN',
      category: ticket.category || 'GENERAL',
      priority: (ticket.priority as TicketPriority) || 'NORMAL',
      subject: ticket.subject || '',
      message: ticket.message || '',
      internalNotes: null,
      createdAt: ticket.created_at,
      updatedAt: ticket.updated_at,
      unreadCount: ticket.unread_count || 0,
    }));
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('[supportService] Erreur listMyTickets:', err);
    }
    return [];
  }
}

export async function listAllTickets(): Promise<TicketRow[]> {
  try {
    const { data: ticketsData, error: ticketsError } = await supabase
      .from('tickets')
      .select('id, user_id, status, category, priority, subject, message, internal_notes, created_at, updated_at, admin_unread')
      .order('updated_at', { ascending: false })
      .limit(200);

    if (ticketsError) {
      if (import.meta.env.DEV) {
        console.error('[supportService] Erreur récupération tous tickets:', ticketsError);
      }
      return [];
    }

    if (!ticketsData || ticketsData.length === 0) return [];

    const userIds = [...new Set(ticketsData.map((t) => t.user_id).filter(Boolean))];

    // ✅ Chercher les profils par user_id (et non par id)
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('user_id, email, full_name, phone, city')
      .in('user_id', userIds);

    if (profilesError && import.meta.env.DEV) {
      console.warn('[supportService] Erreur récupération profils:', profilesError.message);
    }

    // ✅ Map par user_id
    const profilesMap = new Map((profilesData || []).map((p) => [p.user_id, p]));

    return ticketsData.map((ticket) => {
      const profile = ticket.user_id ? profilesMap.get(ticket.user_id) : null;
      return {
        id: ticket.id,
        userId: ticket.user_id,
        status: (ticket.status as TicketStatus) || 'OPEN',
        category: ticket.category || 'GENERAL',
        priority: (ticket.priority as TicketPriority) || 'NORMAL',
        subject: ticket.subject || '',
        message: ticket.message || '',
        internalNotes: ticket.internal_notes || null,
        createdAt: ticket.created_at,
        updatedAt: ticket.updated_at,
        email: profile?.email || null,
        fullName: profile?.full_name || null,
        phone: profile?.phone || null,
        city: profile?.city || null,
        adminUnread: ticket.admin_unread || false,
      };
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('[supportService] Erreur listAllTickets:', err);
    }
    return [];
  }
}

export async function getTicketMessages(ticketId: number): Promise<TicketMessage[]> {
  try {
    const { data, error } = await supabase
      .from('ticket_messages')
      .select('id, ticket_id, user_id, is_admin, message, created_at')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (error) {
      if (import.meta.env.DEV) {
        console.error('[supportService] Erreur récupération messages:', error);
      }
      return [];
    }

    return (data || []).map((msg) => ({
      id: msg.id,
      ticketId: msg.ticket_id,
      userId: msg.user_id,
      isAdmin: msg.is_admin,
      message: msg.message,
      createdAt: msg.created_at,
    }));
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('[supportService] Erreur getTicketMessages:', err);
    }
    return [];
  }
}

export async function addTicketMessage(ticketId: number, message: string, isAdmin: boolean = false): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utilisateur non authentifié');

    const { error: msgError } = await supabase
      .from('ticket_messages')
      .insert({
        ticket_id: ticketId,
        user_id: user.id,
        is_admin: isAdmin,
        message: message.trim(),
      });

    if (msgError) {
      if (import.meta.env.DEV) {
        console.error('[supportService] Erreur ajout message:', msgError);
      }
      throw new Error('Impossible d\'ajouter le message');
    }

    if (isAdmin) {
      const { data: ticket } = await supabase
        .from('tickets')
        .select('unread_count')
        .eq('id', ticketId)
        .maybeSingle();

      const currentCount = ticket?.unread_count || 0;

      await supabase
        .from('tickets')
        .update({ 
          unread_count: currentCount + 1,
          admin_unread: false,
          updated_at: new Date().toISOString() 
        })
        .eq('id', ticketId);
    } else {
      await supabase
        .from('tickets')
        .update({ 
          admin_unread: true,
          updated_at: new Date().toISOString() 
        })
        .eq('id', ticketId);

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('user_id', user.id)
        .maybeSingle();

      const { data: ticket } = await supabase
        .from('tickets')
        .select('subject')
        .eq('id', ticketId)
        .maybeSingle();

      if (profile?.email && profile?.full_name && ticket?.subject) {
        try {
          const emailPayload = prepareNewTicketMessageAdminEmail({
            ticketId,
            userName: profile.full_name,
            userEmail: profile.email,
            subject: ticket.subject,
            message: message.trim(),
          });
          await sendTransactionalEmail(emailPayload);
          if (import.meta.env.DEV) {
            console.log(`[supportService] ✅ Email envoyé à l'admin pour nouveau message ticket #${ticketId}`);
          }
        } catch (emailError) {
          if (import.meta.env.DEV) {
            console.warn('[supportService] ⚠️ Erreur envoi email admin nouveau message:', emailError);
          }
        }
      }
    }

    await logAdmin('TICKET_MESSAGE', 'ticket', ticketId, { isAdmin }).catch(() => {});
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('Erreur inconnue lors de l\'ajout du message');
  }
}

export async function markTicketAsRead(ticketId: number): Promise<void> {
  try {
    const { error } = await supabase
      .from('tickets')
      .update({ unread_count: 0 })
      .eq('id', ticketId);

    if (error && import.meta.env.DEV) {
      console.error('[supportService] Erreur marquage lu:', error);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('[supportService] Erreur markTicketAsRead:', err);
    }
  }
}

export async function markTicketAsReadByAdmin(ticketId: number): Promise<void> {
  try {
    const { error } = await supabase
      .from('tickets')
      .update({ admin_unread: false })
      .eq('id', ticketId);

    if (error && import.meta.env.DEV) {
      console.error('[supportService] Erreur marquage lu admin:', error);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('[supportService] Erreur markTicketAsReadByAdmin:', err);
    }
  }
}

export async function setStatus(ticketId: number, status: TicketStatus): Promise<void> {
  try {
    const { error } = await supabase
      .from('tickets')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', ticketId);

    if (error) {
      if (import.meta.env.DEV) {
        console.error('[supportService] Erreur mise à jour statut ticket:', error);
      }
      throw new Error('Impossible de mettre à jour le statut');
    }

    await logAdmin('TICKET_STATUS', 'ticket', ticketId, { status }).catch(() => {});
    await postEventSilent('ticket.status', { ticketId, status });
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('Erreur inconnue lors de la mise à jour du statut');
  }
}

export async function setInternalNotes(ticketId: number, notes: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('tickets')
      .update({ internal_notes: notes, updated_at: new Date().toISOString() })
      .eq('id', ticketId);

    if (error) {
      if (import.meta.env.DEV) {
        console.error('[supportService] Erreur mise à jour notes ticket:', error);
      }
      throw new Error('Impossible de mettre à jour les notes');
    }

    await logAdmin('TICKET_NOTES', 'ticket', ticketId, { hasNotes: notes.trim().length > 0 }).catch(() => {});
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('Erreur inconnue lors de la mise à jour des notes');
  }
}

export const supportService = {
  createTicket,
  listMyTickets,
  listAllTickets,
  getTicketMessages,
  addTicketMessage,
  markTicketAsRead,
  markTicketAsReadByAdmin,
  setStatus,
  setInternalNotes,
};